<div class="w-full mx-auto h-screen p-5">
  
  <!-- Loading Skeleton -->
  <div *ngIf="!projectData" class="grid grid-cols-1 gap-4 xs:gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8">
    <div *ngFor="let i of [1,2,3,4,5,6]" class="animate-pulse">
      <div class="relative overflow-hidden rounded-xl border border-stroke bg-white p-5 xs:p-6 shadow-default dark:border-strokedark dark:bg-boxdark min-h-[160px]">
        <div class="flex items-center justify-between">
          <div class="flex-1 pr-4">
            <div class="h-4 bg-gray-200 rounded dark:bg-gray-700 mb-3 w-2/3"></div>
            <div class="h-8 bg-gray-200 rounded dark:bg-gray-700 w-3/4"></div>
          </div>
          <div class="h-12 w-12 xs:h-14 xs:w-14 bg-gray-200 rounded-full dark:bg-gray-700 flex-shrink-0"></div>
        </div>
        <div class="mt-4">
          <div class="h-3 bg-gray-200 rounded dark:bg-gray-700 mb-2 w-1/2"></div>
          <div class="h-2 bg-gray-200 rounded-full dark:bg-gray-700 w-full"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Stats Dashboard - Layout Responsive Optimizado -->
  <div *ngIf="projectData" class="grid grid-cols-1 gap-4 xs:gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8 animate-fade-in">
    
    <!-- Primera Fila: Métricas Principales de Ingresos y Gastos -->
    <!-- Ordenados estratégicamente para mejor comprensión visual -->
    <!-- Ingresos -->
    <div class="group relative overflow-hidden rounded-xl border border-stroke bg-gradient-to-br from-green-50 to-white p-6 shadow-default transition-all duration-300 hover:shadow-xl hover:scale-105 dark:border-strokedark dark:from-green-900/10 dark:to-boxdark cursor-pointer min-h-[160px] flex flex-col justify-between"
         [title]="getIncomeTooltip()"
         (click)="scrollToSection('income-section')">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Ingresos Totales (COP)</p>
          <h3 class="mt-2 text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 group-hover:text-green-600">
            {{ totalIncomeFromReceipts | currency }}
          </h3>
          <p class="text-xs text-blue-600 dark:text-blue-400 mt-1 opacity-75">
            ✓ Convertido con TRM
          </p>
        </div>
        <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
          <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </div>
      
      <!-- Barra de progreso -->
      <div class="mt-4">
        <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
          <span>Progreso</span>
          <span class="flex items-center" [ngClass]="getIncomeProgressColor(totalIncomeFromReceipts, projectData?.projectedRevenue)">
            <svg *ngIf="getProgressIcon(totalIncomeFromReceipts, projectData?.projectedRevenue, true) === 'up'" class="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" transform="rotate(180 10 10)"/>
            </svg>
            <svg *ngIf="getProgressIcon(totalIncomeFromReceipts, projectData?.projectedRevenue, true) === 'down'" class="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
            {{ calculatePercentage(totalIncomeFromReceipts, projectData?.projectedRevenue) }}%
          </span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
          <div class="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-700 ease-out progress-bar" 
               [style.width.%]="Math.min(getPercentageValue(totalIncomeFromReceipts, projectData?.projectedRevenue), 100)">
          </div>
        </div>
      </div>
      
      <!-- Efecto de brillo en hover -->
      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
    </div>
  
    <!-- Gastos -->
    <div class="group relative overflow-hidden rounded-xl border border-stroke bg-gradient-to-br from-red-50 to-white p-6 shadow-default transition-all duration-300 hover:shadow-xl hover:scale-105 dark:border-strokedark dark:from-red-900/10 dark:to-boxdark cursor-pointer min-h-[160px] flex flex-col justify-between"
         [title]="getExpenseTooltip()"
         (click)="scrollToSection('expenses-section')">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Gastos Ejecutados</p>
          <h3 class="mt-2 text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 group-hover:text-red-600">
            {{ totalExecutedAmount | currency }}
          </h3>
        </div>
        <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-red-400 to-red-600 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
          <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
          </svg>
        </div>
      </div>
      
      <!-- Barra de progreso -->
      <div class="mt-4">
        <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
          <span>Ejecución</span>
          <span class="flex items-center" [ngClass]="getExpenseProgressColor(totalExecutedAmount, projectData?.projectedSpend)">
            <svg *ngIf="getProgressIcon(totalExecutedAmount, projectData?.projectedSpend, false) === 'up'" class="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" transform="rotate(180 10 10)"/>
            </svg>
            <svg *ngIf="getProgressIcon(totalExecutedAmount, projectData?.projectedSpend, false) === 'neutral'" class="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
            <svg *ngIf="getProgressIcon(totalExecutedAmount, projectData?.projectedSpend, false) === 'down'" class="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
            {{ calculatePercentage(totalExecutedAmount, projectData?.projectedSpend) }}%
          </span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
          <div class="h-2 rounded-full transition-all duration-700 ease-out progress-bar"
               [class]="getPercentageValue(totalExecutedAmount, projectData?.projectedSpend) <= 80 ? 'bg-gradient-to-r from-green-400 to-green-600' : getPercentageValue(totalExecutedAmount, projectData?.projectedSpend) <= 100 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 'bg-gradient-to-r from-red-400 to-red-600'"
               [style.width.%]="Math.min(getPercentageValue(totalExecutedAmount, projectData?.projectedSpend), 100)">
          </div>
        </div>
      </div>
      
      <!-- Efecto de brillo en hover -->
      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
    </div>
  
    <!-- Ingresos Proyectados -->
    <div class="group relative overflow-hidden rounded-xl border border-stroke bg-gradient-to-br from-blue-50 to-white p-6 shadow-default transition-all duration-300 hover:shadow-xl hover:scale-105 dark:border-strokedark dark:from-blue-900/10 dark:to-boxdark min-h-[160px] flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Ingresos Proyectados</p>
          <h3 class="mt-2 text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 group-hover:text-blue-600">
            {{ projectData?.projectedRevenue | currency }}
          </h3>
        </div>
        <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
          <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
          </svg>
        </div>
      </div>
      <div class="mt-4 text-xs text-gray-600 dark:text-gray-400 font-medium">
        🎯 Meta de ingresos del proyecto
      </div>
    </div>
  
    <!-- Gastos Proyectados -->
    <div class="group relative overflow-hidden rounded-xl border border-stroke bg-gradient-to-br from-purple-50 to-white p-6 shadow-default transition-all duration-300 hover:shadow-xl hover:scale-105 dark:border-strokedark dark:from-purple-900/10 dark:to-boxdark min-h-[160px] flex flex-col justify-between">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Gastos Proyectados</p>
          <h3 class="mt-2 text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 group-hover:text-purple-600">
            {{ projectData?.projectedSpend | currency }}
          </h3>
        </div>
        <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-400 to-purple-600 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
          <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/>
          </svg>
        </div>
      </div>
      <div class="mt-4 flex items-center text-xs font-medium">
        <span class="text-gray-600 dark:text-gray-400">💰 Disponible:</span>
        <span class="ml-2 px-2 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
          {{ (projectData?.projectedSpend - totalExecutedAmount) | currency }}
        </span>
      </div>
    </div>
  
    <!-- Excesos de Pagos -->
    <div class="group relative overflow-hidden rounded-xl border border-stroke bg-gradient-to-br from-orange-50 to-white p-6 shadow-default transition-all duration-300 hover:shadow-xl hover:scale-105 dark:border-strokedark dark:from-orange-900/10 dark:to-boxdark cursor-pointer min-h-[160px] flex flex-col justify-between"
         [title]="getExcessTooltip()"
         (click)="scrollToSection('excess-payments-section')">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Excesos de Pagos</p>
          <h3 class="mt-2 text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 group-hover:text-orange-600">
            {{ totalExcessPayments | currency }}
          </h3>
        </div>
        <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
          <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v12m6-6H6"/>
          </svg>
        </div>
      </div>
      <div class="mt-4 flex items-center justify-between">
        <div class="flex items-center text-xs font-medium">
          <span class="text-gray-600 dark:text-gray-400">📊 Total registros:</span>
          <span class="ml-2 px-2 py-1 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400">
            {{ excessPaymentsData.length }}
          </span>
        </div>
      </div>
      
      <!-- Efecto de brillo en hover -->
      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
    </div>
  
    <!-- Devoluciones -->
    <div class="group relative overflow-hidden rounded-xl border border-stroke bg-gradient-to-br from-indigo-50 to-white p-6 shadow-default transition-all duration-300 hover:shadow-xl hover:scale-105 dark:border-strokedark dark:from-indigo-900/10 dark:to-boxdark cursor-pointer min-h-[160px] flex flex-col justify-between"
         [title]="getRefundTooltip()"
         (click)="scrollToSection('refund-vouchers-section')">
      <div class="flex items-center justify-between">
        <div>
          <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Total Devoluciones</p>
          <h3 class="mt-2 text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 group-hover:text-indigo-600">
            {{ totalRefundVouchers | currency }}
          </h3>
        </div>
        <div class="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">
          <svg class="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 15l6-6m-6 0l6 6M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
      </div>
      <div class="mt-4 flex items-center justify-between">
        <div class="flex items-center text-xs font-medium">
          <span class="text-gray-600 dark:text-gray-400">🔄 Total procesadas:</span>
          <span class="ml-2 px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400">
            {{ refundVouchersData.length }}
          </span>
        </div>
      </div>
      
      <!-- Efecto de brillo en hover -->
      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transform -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
    </div>
  </div>

  <!-- Detalles del proyecto -->
  <div class="mt-8 rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="flex justify-between items-center mb-6">
      <h4 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
        <svg class="h-6 w-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        Detalles del Proyecto
      </h4>
      <button 
        type="button" 
        (click)="handleEditarProyecto(projectData?.id)" 
        class="group relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-medium text-white bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg shadow-lg transition-all duration-300 ease-out hover:scale-105 hover:shadow-xl hover:from-yellow-500 hover:to-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2">
        <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
        </svg>
        Editar Proyecto
      </button>
      <button
        *ngIf="canDelete"
        type="button"
        (click)="openDelete()"
        class="ml-3 px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2">
        Eliminar proyecto
      </button>
      <app-project-deletion-confirm #deleteModal [projectId]="projectData?.id"></app-project-deletion-confirm>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <!-- Información básica -->
      <div class="group bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100 hover:shadow-lg transition-all duration-300 dark:from-blue-900/10 dark:to-boxdark dark:border-blue-800/30">
        <div class="flex items-center mb-3">
          <div class="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3 dark:bg-blue-900/20">
            <svg class="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
            </svg>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Identificación</p>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">{{projectData?.name}}</h3>
          </div>
        </div>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-300">Nemónico:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{projectData?.projectId}}</span>
          </div>
          <div>
            <span class="text-sm text-gray-600 dark:text-gray-300">Descripción:</span>
            <p class="text-sm text-gray-900 dark:text-white mt-1">{{projectData?.description}}</p>
          </div>
        </div>
    </div>

      <!-- Estado y periodo -->
      <div class="group bg-gradient-to-br from-green-50 to-white p-5 rounded-xl border border-green-100 hover:shadow-lg transition-all duration-300 dark:from-green-900/10 dark:to-boxdark dark:border-green-800/30">
        <div class="flex items-center mb-3">
          <div class="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center mr-3 dark:bg-green-900/20">
            <svg class="h-5 w-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Estado del Proyecto</p>
            <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" 
                  [ngClass]="{
                    'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400': projectData?.status === 'CREATED',
                    'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400': projectData?.status === 'PRECLOSED',
                    'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400': projectData?.status === 'CLOSED',
                    'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400': projectData?.status === 'CANCELED' || projectData?.status === 'INACTIVE'
                  }">
              {{getStatusProject(projectData?.status)}}
            </span>
          </div>
        </div>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-300">Periodo:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{projectData?.period}}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-300">Duración:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{projectData?.duration}} Semanas</span>
          </div>
        </div>
      </div>

      <!-- Fechas importantes -->
      <div class="group bg-gradient-to-br from-purple-50 to-white p-5 rounded-xl border border-purple-100 hover:shadow-lg transition-all duration-300 dark:from-purple-900/10 dark:to-boxdark dark:border-purple-800/30">
        <div class="flex items-center mb-3">
          <div class="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3 dark:bg-purple-900/20">
            <svg class="h-5 w-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Cronograma</p>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">Fechas Clave</h3>
          </div>
        </div>
        <div class="space-y-2">
          <div class="flex justify-between">
            <span class="text-sm text-gray-600 dark:text-gray-300">Inicio:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{projectData?.date | date:'short'}}</span>
          </div>
          <div class="flex justify-between" *ngIf="projectData?.dateClosed">
            <span class="text-sm text-gray-600 dark:text-gray-300">Cierre:</span>
            <span class="text-sm font-medium text-gray-900 dark:text-white">{{projectData?.dateClosed | date:'short'}}</span>
          </div>
          <div *ngIf="projectData?.observations" class="mt-3 pt-3 border-t border-purple-200 dark:border-purple-800/30">
            <span class="text-sm text-gray-600 dark:text-gray-300">Observaciones:</span>
            <p class="text-sm text-gray-900 dark:text-white mt-1">{{projectData?.observations}}</p>
          </div>
        </div>
      </div>

      <!-- Información de Inscripción -->
      <div class="group bg-gradient-to-br from-yellow-50 to-white p-5 rounded-xl border border-yellow-100 hover:shadow-lg transition-all duration-300 dark:from-yellow-900/10 dark:to-boxdark dark:border-yellow-800/30">
        <div class="flex items-center mb-3">
          <div class="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center mr-3 dark:bg-yellow-900/20">
            <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Costos de Inscripción</p>            
          </div>
        </div>
        <div class="space-y-3">
          <!-- Monto Principal (basado en la moneda del proyecto) -->
          <div class="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div class="flex justify-between items-center">
              <div>
                <span class="text-sm font-medium text-blue-600 dark:text-blue-400">Monto de Inscripción:</span>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Moneda del proyecto: {{ getProjectCurrencyName() }}
                </div>
              </div>
              <div class="text-right">
                <span class="text-xl font-bold text-blue-900 dark:text-blue-300">
                  {{ getProjectInscriptionAmount() }}
                </span>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ projectData?.currency?.code || 'COP' }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Monto Equivalente (si es diferente a COP) -->
          <div *ngIf="shouldShowEquivalentAmount()" class="bg-gray-50 p-3 rounded-lg dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700">
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Equivalente en COP:</span>
              <span class="text-lg font-bold text-gray-900 dark:text-gray-300">
                {{ getEquivalentAmountCOP() }}
              </span>
            </div>
          </div>
          
          <!-- Información adicional de tasa de cambio -->
          <div *ngIf="projectData?.currentExchangeRate" class="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-yellow-200 dark:border-yellow-800/30">
            <div class="flex justify-between mb-1">
              <span>Tasa de cambio:</span>
              <span class="font-medium">1 {{ projectData?.currency?.code || 'USD' }} = {{ projectData?.currentExchangeRate | number:'1.2-2' }} COP</span>
            </div>
            <div *ngIf="projectData?.lastExchangeRateUpdate" class="text-xs text-gray-400">
              Última actualización: {{ projectData?.lastExchangeRateUpdate | date:'short' }}
            </div>
          </div>

          <!-- Indicador de equivalencia cuando hay ambos montos -->
          <div *ngIf="!projectData?.currentExchangeRate && projectData?.inscriptionAmountUSD && projectData?.inscriptionAmountCOP" class="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-yellow-200 dark:border-yellow-800/30">
            <div class="flex justify-between">
              <span>Equivalencia aprox:</span>
              <span class="font-medium">1 USD ≈ {{ (projectData?.inscriptionAmountCOP / projectData?.inscriptionAmountUSD) | number:'1.0-0' }} COP</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Participantes del proyecto -->
  <div class="mt-8 rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="flex items-center justify-between mb-6">
      <h4 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
        <svg class="h-6 w-6 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
        </svg>
        Participantes del Proyecto
      </h4>
      <div class="flex items-center space-x-3">
        <span class="text-sm text-gray-500 dark:text-gray-400">
          Total: <span class="font-medium text-indigo-600 dark:text-indigo-400">{{getTotalParticipants()}}</span>
        </span>
        <div class="h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          Individuales: <span class="font-medium text-green-600 dark:text-green-400">{{individualEnrollments?.length || 0}}</span>
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          Corporativos: <span class="font-medium text-blue-600 dark:text-blue-400">{{corporateParticipants?.length || 0}}</span>
        </span>
        <span class="text-xs text-gray-500 dark:text-gray-400">
          Empresas: <span class="font-medium text-purple-600 dark:text-purple-400">{{getTotalCorporateEnrollments()}}</span>
        </span>
      </div>
    </div>

    <!-- Grid de participantes para pantallas grandes -->
    <div class="hidden lg:block">
      <!-- Participantes Individuales -->
      <div *ngIf="individualEnrollments?.length > 0" class="mb-8">
        <h5 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <div class="h-8 w-8 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
            {{individualEnrollments?.length}}
          </div>
          Inscripciones Individuales
        </h5>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div *ngFor="let enrollment of individualEnrollments" 
               class="group bg-gradient-to-br from-green-50 to-white p-5 rounded-xl border border-green-200 hover:shadow-lg hover:border-green-300 transition-all duration-300 dark:from-green-800/50 dark:to-boxdark dark:border-green-700 dark:hover:border-green-500">
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center">
                <div class="h-12 w-12 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-medium text-lg shadow-lg">
                  {{enrollment.firstName?.charAt(0)}}{{enrollment.lastName?.charAt(0)}}
                </div>
                <div class="ml-3">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
                    {{enrollment.firstName}} {{enrollment.lastName}}
                  </h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">{{enrollment.documentId}}</p>
                </div>
              </div>
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                <svg class="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="3"/>
                </svg>
                {{enrollment.paymentStatus}}
              </span>
            </div>
            
            <div class="space-y-2 mb-4">
              <div class="flex items-center text-sm text-gray-600 dark:text-gray-300">
                <svg class="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                {{enrollment.email}}
              </div>
              <div *ngIf="enrollment.companyName" class="flex items-center text-sm text-gray-600 dark:text-gray-300">
                <svg class="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
                {{enrollment.companyName}}
              </div>
              <div class="flex items-center text-sm font-medium text-green-600 dark:text-green-400">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                {{enrollment.totalAmount | currency}}
              </div>
            </div>

            <div class="flex justify-end">
              <a [routerLink]="['/dashboard/participants/profile-participant', enrollment.participantId]" 
                 target="_blank"
                 class="inline-flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-400">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                Ver Detalles
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Inscripciones Corporativas -->
      <div *ngIf="corporateEnrollments?.length > 0" class="mb-8">
        <h5 class="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <div class="h-8 w-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
            {{getTotalCorporateEnrollments()}}
          </div>
          Inscripciones Corporativas
        </h5>
        <div class="space-y-6">
          <div *ngFor="let corporate of corporateEnrollments" 
               class="group bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl border border-blue-200 hover:shadow-lg hover:border-blue-300 transition-all duration-300 dark:from-blue-800/50 dark:to-boxdark dark:border-blue-700 dark:hover:border-blue-500">
            <!-- Header de la empresa -->
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center">
                <div class="h-12 w-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-medium text-lg shadow-lg">
                  {{corporate.companyName?.charAt(0)}}
                </div>
                <div class="ml-3">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {{corporate.companyName}}
                  </h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">Inscripción Corporativa</p>
                </div>
              </div>
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                    [ngClass]="corporate.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'">
                <svg class="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="3"/>
                </svg>
                {{corporate.isActive ? 'Activa' : 'Inactiva'}}
              </span>
            </div>

            <!-- Información de la inscripción corporativa -->
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div class="bg-blue-100 p-3 rounded-lg dark:bg-blue-900/20">
                <p class="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">% Cobertura</p>
                <p class="text-lg font-bold text-blue-900 dark:text-blue-300">{{corporate.coveragePercentage}}%</p>
              </div>
              <div class="bg-gray-100 p-3 rounded-lg dark:bg-gray-900/20">
                <p class="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Participantes</p>
                <p class="text-lg font-bold text-gray-900 dark:text-gray-300">
                  {{countCorporateParticipantsByEnrollment(corporate.id)}}
                </p>
              </div>
            </div>

            <!-- Lista de participantes corporativos -->
            <div *ngIf="countCorporateParticipantsByEnrollment(corporate.id) > 0" class="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800/30">
              <h6 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Participantes de esta empresa:</h6>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div *ngFor="let participant of getCorporateParticipantsByEnrollment(corporate.id)" 
                     class="flex items-center p-3 bg-white dark:bg-boxdark rounded-lg border border-gray-200 dark:border-gray-700">
                  <div class="h-8 w-8 bg-gradient-to-br from-blue-300 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {{participant.firstName?.charAt(0)}}{{participant.lastName?.charAt(0)}}
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {{participant.firstName}} {{participant.lastName}}
                    </p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{participant.documentId}}</p>
                  </div>
                  <a [routerLink]="['/dashboard/participants/profile-participant', participant.participantId]" 
                     target="_blank"
                     class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mensaje si no hay participantes -->
      <div *ngIf="getTotalParticipants() === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay participantes</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Este proyecto aún no tiene participantes registrados.</p>
      </div>
    </div>

    <!-- Tabla para pantallas medianas y pequeñas -->
    <div class="lg:hidden">
      <div class="overflow-x-auto">
        <table class="w-full" *ngIf="getTotalParticipants() > 0; else noParticipants2">
        <thead>
            <tr class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
                Participante
            </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Correo
            </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Tipo
            </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Acciones
            </th>
          </tr>
        </thead>
          <tbody class="bg-white divide-y divide-gray-200 dark:bg-boxdark dark:divide-gray-700">
            <!-- Participantes individuales -->
            <tr *ngFor="let enrollment of individualEnrollments" 
                class="hover:bg-green-50 dark:hover:bg-green-800/50 transition-colors duration-200">
              <td class="px-4 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="h-10 w-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-medium text-sm shadow-lg">
                    {{enrollment.firstName?.charAt(0)}}{{enrollment.lastName?.charAt(0)}}
                  </div>
                  <div class="ml-3">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{enrollment.firstName}} {{enrollment.lastName}}
                    </div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">{{enrollment.documentId}}</div>
                  </div>
                </div>
            </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-white">{{enrollment.email}}</div>
            </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                  <svg class="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 8 8">
                    <circle cx="4" cy="4" r="3"/>
                  </svg>
                Individual
                </span>
            </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <a [routerLink]="['/dashboard/participants/profile-participant', enrollment.participantId]" 
                   target="_blank"
                   class="inline-flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-400">
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
              </a>
            </td>
          </tr>
          <!-- Participantes corporativos -->
          <tr *ngFor="let participant of corporateParticipants" 
              class="hover:bg-blue-50 dark:hover:bg-blue-800/50 transition-colors duration-200">
            <td class="px-4 py-4 whitespace-nowrap">
              <div class="flex items-center">
                <div class="h-10 w-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm shadow-lg">
                  {{participant.firstName?.charAt(0)}}{{participant.lastName?.charAt(0)}}
                </div>
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900 dark:text-white">
                    {{participant.firstName}} {{participant.lastName}}
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">{{participant.companyName}}</div>
                </div>
              </div>
          </td>
            <td class="px-4 py-4 whitespace-nowrap">
              <div class="text-sm text-gray-900 dark:text-white">{{participant.email}}</div>
          </td>
            <td class="px-4 py-4 whitespace-nowrap">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                <svg class="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="3"/>
                </svg>
              Corporativo
              </span>
          </td>
            <td class="px-4 py-4 whitespace-nowrap">
              <a [routerLink]="['/participants/view-participant', participant.participantId]" 
                 target="_blank"
                 class="inline-flex items-center px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
            </a>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    </div>

    <!-- Estado vacío -->
    <ng-template #noParticipants2>
      <div class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay participantes</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Este proyecto aún no tiene participantes registrados.</p>
      </div>
    </ng-template>
  </div>

  <!-- Presupuestos del proyecto -->
  <div class="mt-8 rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="flex justify-between items-center mb-6">
      <h4 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
        <svg class="h-6 w-6 text-emerald-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        Presupuestos del Proyecto
      </h4>
      <button 
        type="button" 
        (click)="handleNuevoPresupuesto()" 
        class="group relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-medium text-white bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-lg shadow-lg transition-all duration-300 ease-out hover:scale-105 hover:shadow-xl hover:from-emerald-500 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2">
        <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
        </svg>
        Agregar Presupuesto
      </button>
    </div>

    <div *ngIf="projectData?.budgets?.length > 0; else noBudgets" class="space-y-4">
      <!-- Cards de presupuesto -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div *ngFor="let budget of projectData?.budgets" 
             class="group bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:shadow-lg hover:border-emerald-300 transition-all duration-300 dark:from-gray-800/50 dark:to-boxdark dark:border-gray-700 dark:hover:border-emerald-500">
          
          <!-- Header del presupuesto -->
          <div class="flex items-start justify-between mb-4">
            <div class="flex items-center">
              <div class="h-12 w-12 rounded-lg flex items-center justify-center mr-3 shadow-lg"
                   [ngClass]="{
                     'bg-gradient-to-br from-green-400 to-green-600': budget.budgetSubcategory.budgetCategory.budgetType === 'INCOME',
                     'bg-gradient-to-br from-red-400 to-red-600': budget.budgetSubcategory.budgetCategory.budgetType === 'EXPENSE'
                   }">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path *ngIf="budget.budgetSubcategory.budgetCategory.budgetType === 'INCOME'" 
                        stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  <path *ngIf="budget.budgetSubcategory.budgetCategory.budgetType === 'EXPENSE'" 
                        stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  {{budget.budgetSubcategory.budgetCategory.name}}
                </h3>
                <p class="text-sm text-gray-500 dark:text-gray-400">{{budget.budgetSubcategory.name}}</p>
              </div>
    </div>

            <div class="flex items-center space-x-2">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                    [ngClass]="{
                      'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400': budget.budgetSubcategory.budgetCategory.budgetType === 'INCOME',
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400': budget.budgetSubcategory.budgetCategory.budgetType === 'EXPENSE'
                    }">
                {{getBudgetTypeName(budget.budgetSubcategory.budgetCategory.budgetType)}}
              </span>
            </div>
          </div>

          <!-- Información financiera -->
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="bg-blue-50 p-3 rounded-lg dark:bg-blue-900/20">
              <p class="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Proyectado</p>
              <p class="text-lg font-bold text-blue-900 dark:text-blue-300">{{budget.projectedAmount | currency}}</p>
            </div>
            <div class="bg-green-50 p-3 rounded-lg dark:bg-green-900/20">
              <p class="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Ejecutado</p>
              <p class="text-lg font-bold text-green-900 dark:text-green-300">{{budget.executedAmount | currency}}</p>
            </div>
          </div>

          <!-- Barra de progreso -->
          <div class="mb-4">
            <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
              <span>Ejecución Presupuestal</span>
              <span class="font-medium" 
                    [ngClass]="{
                      'text-green-600 dark:text-green-400': getPercentageValue(budget.executedAmount, budget.projectedAmount) <= 80,
                      'text-yellow-600 dark:text-yellow-400': getPercentageValue(budget.executedAmount, budget.projectedAmount) > 80 && getPercentageValue(budget.executedAmount, budget.projectedAmount) <= 100,
                      'text-red-600 dark:text-red-400': getPercentageValue(budget.executedAmount, budget.projectedAmount) > 100
                    }">
                {{calculatePercentage(budget.executedAmount, budget.projectedAmount)}}%
              </span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div class="h-2.5 rounded-full transition-all duration-700 ease-out"
                   [ngClass]="{
                     'bg-gradient-to-r from-green-400 to-green-600': getPercentageValue(budget.executedAmount, budget.projectedAmount) <= 80,
                     'bg-gradient-to-r from-yellow-400 to-yellow-600': getPercentageValue(budget.executedAmount, budget.projectedAmount) > 80 && getPercentageValue(budget.executedAmount, budget.projectedAmount) <= 100,
                     'bg-gradient-to-r from-red-400 to-red-600': getPercentageValue(budget.executedAmount, budget.projectedAmount) > 100
                   }"
                   [style.width.%]="Math.min(getPercentageValue(budget.executedAmount, budget.projectedAmount), 100)">
              </div>
            </div>
          </div>

          <!-- Footer con información adicional y acciones -->
          <div class="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div class="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                </svg>
                ID: {{budget.id}}
              </div>
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                </svg>
                Qty: {{budget.qty}}
              </div>
            </div>
            
            <div class="flex items-center gap-2">
              <button
                type="button"
                (click)="handleEditarPresupuesto(budget.id)"
                class="inline-flex items-center px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 dark:text-emerald-400">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
                Editar
              </button>

              <!-- 🆕 Botón Eliminar rubro -->
              <button
                type="button"
                (click)="handleEliminarPresupuesto(budget)"
                title="Eliminar rubro (sólo si no tiene relaciones)"
                class="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/>
                </svg>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado vacío -->
    <ng-template #noBudgets>
      <div class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay presupuestos configurados</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Comenzá agregando presupuestos para este proyecto.</p>
        <button 
          type="button" 
          (click)="handleNuevoPresupuesto()" 
          class="mt-4 inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-200">
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          Agregar Primer Presupuesto
        </button>
    </div>
    </ng-template>
  </div>

  <!-- Ingresos del proyecto -->
  <div id="income-section" class="mt-8 rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="flex items-center justify-between mb-6">
      <h4 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
        <svg class="h-6 w-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        Ingresos del Proyecto
      </h4>
      <div class="flex items-center space-x-4">
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Total Ingresos (COP)</p>
          <p class="text-xl font-bold text-green-600 dark:text-green-400">{{totalIncomeFromReceipts | currency}}</p>
          <p class="text-xs text-blue-600 dark:text-blue-400 mt-1">
            <svg class="h-3 w-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            Convertido con TRM
          </p>
        </div>
        <div class="h-8 w-px bg-gray-300 dark:bg-gray-600"></div>
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Registros</p>
          <p class="text-xl font-bold text-gray-900 dark:text-white">{{incomeReceiptsData?.length || 0}}</p>
        </div>
      </div>
    </div>

    <div *ngIf="incomeReceiptsData?.length > 0; else noIncomeReceipts" class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                </svg>
                ID & Participante
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Concepto
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Montos & Moneda
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Pago & Fecha
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200 dark:bg-boxdark dark:divide-gray-700">
          <tr *ngFor="let receipt of incomeReceiptsData" 
              class="hover:bg-green-50 dark:hover:bg-green-900/10 transition-colors duration-200">
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="flex items-center">
                <div class="h-10 w-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-medium text-sm shadow-lg">
                  {{receipt.participant?.firstName?.charAt(0)}}{{receipt.participant?.lastName?.charAt(0)}}
                </div>
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900 dark:text-white">
                    {{receipt.participant?.firstName}} {{receipt.participant?.lastName}}
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">ID: {{receipt.id}}</div>
                </div>
              </div>
            </td>
            
            <td class="px-4 py-5">
              <div class="text-sm text-gray-900 dark:text-white font-medium">{{receipt.concept}}</div>
            </td>
            
            <td class="px-4 py-5">
              <div class="space-y-1">
                <!-- Montos convertidos a COP (principales) -->
                <div class="flex items-center justify-between">
                  <span class="text-xs text-gray-500 dark:text-gray-400">Bruto (COP):</span>
                  <span class="text-sm font-medium text-gray-900 dark:text-white">{{receipt.grossAmount | currency}}</span>
                </div>
                <div class="flex items-center justify-between" *ngIf="receipt.totalFees > 0">
                  <span class="text-xs text-red-500">Descuentos (COP):</span>
                  <span class="text-sm font-medium text-red-600">-{{receipt.totalFees | currency}}</span>
                </div>
                <div class="flex items-center justify-between pt-1 border-t border-gray-200 dark:border-gray-600">
                  <span class="text-xs font-medium text-green-600 dark:text-green-400">Neto (COP):</span>
                  <span class="text-sm font-bold text-green-600 dark:text-green-400">{{receipt.netAmount | currency}}</span>
                </div>
                
                <!-- 🆕 Montos originales (antes de conversión) -->
                <div *ngIf="shouldShowOriginalAmount(receipt)" class="mt-2 pt-2 border-t border-purple-100 dark:border-purple-800/30">
                  <div class="bg-purple-50 dark:bg-purple-900/20 p-2 rounded-lg">
                    <div class="flex items-center text-xs text-purple-600 dark:text-purple-400 mb-2">
                      <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      Montos Originales
                    </div>
                    <div class="space-y-1 text-xs">
                      <div class="flex items-center justify-between">
                        <span class="text-gray-600 dark:text-gray-400">Bruto:</span>
                        <span class="font-medium text-purple-700 dark:text-purple-300">{{getOriginalAmount(receipt, 'gross')}}</span>
                      </div>
                      <div *ngIf="receipt.originalTotalFees > 0" class="flex items-center justify-between">
                        <span class="text-gray-600 dark:text-gray-400">Descuentos:</span>
                        <span class="font-medium text-red-600 dark:text-red-400">-{{getOriginalAmount(receipt, 'fees')}}</span>
                      </div>
                      <div class="flex items-center justify-between pt-1 border-t border-purple-200 dark:border-purple-700">
                        <span class="font-medium text-purple-600 dark:text-purple-400">Neto:</span>
                        <span class="font-bold text-purple-700 dark:text-purple-300">{{getOriginalAmount(receipt, 'net')}}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 🆕 Información de TRM -->
                <div *ngIf="shouldShowCurrencyConversion(receipt)" class="mt-2 pt-2 border-t border-blue-100 dark:border-blue-800/30">
                  <div class="bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
                    <div class="flex items-center text-xs text-blue-600 dark:text-blue-400 mb-1">
                      <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                      </svg>
                      Conversión de Moneda
                    </div>
                    <div class="text-xs text-blue-700 dark:text-blue-300">
                      {{getCurrencyConversionInfo(receipt)}}
                    </div>
                    <div *ngIf="hasExchangeRate(receipt)" class="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Moneda original: {{getReceiptCurrencyName(receipt)}}
                    </div>
                  </div>
                </div>
                
                <!-- Detalles de descuentos -->
                <div *ngIf="receipt.feeDetails && receipt.feeDetails.length > 0" class="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <details class="group">
                    <summary class="cursor-pointer text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
                      Ver detalles descuentos ({{receipt.feeDetails.length}})
                    </summary>
                    <div class="mt-1 space-y-1">
                      <div *ngFor="let fee of receipt.feeDetails" class="flex justify-between text-xs">
                        <span class="text-gray-600 dark:text-gray-400">{{getFeeTypeName(fee)}}:</span>
                        <span class="text-red-600 dark:text-red-400">{{fee.amount | currency}}</span>
                      </div>
                    </div>
                  </details>
                </div>
              </div>
            </td>
            
            <td class="px-4 py-5">
              <div class="space-y-1">
                <div class="flex items-center text-sm text-gray-600 dark:text-gray-300">
                  <svg class="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                  </svg>
                  {{receipt.paymentMethod?.name}}
                </div>
                <div class="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <svg class="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  {{receipt.transactionDate | date:'short'}}
              </div>
              </div>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <a [routerLink]="['/income-receipts/view', receipt.id]" 
                 target="_blank"
                 class="inline-flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 dark:text-green-400">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                Ver
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Estado vacío -->
    <ng-template #noIncomeReceipts>
      <div class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay ingresos registrados</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Este proyecto aún no tiene ingresos registrados.</p>
      </div>
    </ng-template>
  </div>

  <!-- Excesos de Pagos del proyecto -->
  <div id="excess-payments-section" class="mt-8 rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="flex items-center justify-between mb-6">
      <h4 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
        <svg class="h-6 w-6 text-orange-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v12m6-6H6"/>
        </svg>
        Excesos de Pagos
      </h4>
      <div class="flex items-center space-x-4">
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Total Excesos</p>
          <p class="text-xl font-bold text-orange-600 dark:text-orange-400">{{totalExcessPayments | currency}}</p>
        </div>
        <div class="h-8 w-px bg-gray-300 dark:bg-gray-600"></div>
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Registros</p>
          <p class="text-xl font-bold text-gray-900 dark:text-white">{{excessPaymentsData?.length || 0}}</p>
        </div>
      </div>
    </div>

    <div *ngIf="excessPaymentsData?.length > 0; else noExcessPayments" class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20">
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              Participante
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Montos
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              Estado
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                Fecha
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200 dark:bg-boxdark dark:divide-gray-700">
          <tr *ngFor="let excess of excessPaymentsData" 
              class="hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors duration-200">
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="flex items-center">
                <div class="h-10 w-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-medium text-sm shadow-lg">
                  {{excess.participant?.firstName?.charAt(0)}}{{excess.participant?.lastName?.charAt(0)}}
                </div>
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900 dark:text-white">
                    {{excess.participant?.firstName}} {{excess.participant?.lastName}}
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">ID: {{excess.id}}</div>
                </div>
              </div>
            </td>
            
            <td class="px-4 py-5">
              <div class="space-y-2">
                <div class="flex items-center justify-between bg-orange-50 p-2 rounded-lg dark:bg-orange-900/20">
                  <span class="text-xs font-medium text-orange-600 dark:text-orange-400">Exceso Total:</span>
                  <span class="text-sm font-bold text-orange-900 dark:text-orange-300">{{excess.excessAmount | currency}}</span>
                </div>
                <div class="flex items-center justify-between bg-green-50 p-2 rounded-lg dark:bg-green-900/20">
                  <span class="text-xs font-medium text-green-600 dark:text-green-400">Disponible:</span>
                  <span class="text-sm font-bold text-green-900 dark:text-green-300">{{excess.availableAmount | currency}}</span>
                </div>
                <!-- Barra de progreso de uso -->
                <div class="mt-2">
                  <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Utilizado</span>
                    <span>{{calculatePercentage(excess.excessAmount - excess.availableAmount, excess.excessAmount)}}%</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                    <div class="bg-gradient-to-r from-orange-400 to-orange-600 h-1.5 rounded-full transition-all duration-300"
                         [style.width.%]="getPercentageValue(excess.excessAmount - excess.availableAmount, excess.excessAmount)">
                    </div>
                  </div>
                </div>
              </div>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" 
                    [ngClass]="{
                      'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400': excess.status === 'Available',
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400': excess.status === 'PartiallyUsed',
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400': excess.status === 'FullyUsed',
                      'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400': excess.status === 'Expired'
                    }">
                <svg class="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 8 8">
                  <circle cx="4" cy="4" r="3"/>
                </svg>
                {{getExcessPaymentStatusName(excess.status)}}
              </span>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="text-sm text-gray-900 dark:text-white">{{excess.excessDate | date:'short'}}</div>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <a [routerLink]="['/dashboard/excess-payments', excess.id]"
                 target="_blank"
                 class="inline-flex items-center px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 dark:text-orange-400">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                Ver
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Estado vacío -->
    <ng-template #noExcessPayments>
      <div class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v12m6-6H6"/>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay excesos de pagos</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Este proyecto no tiene excesos de pagos registrados.</p>
      </div>
    </ng-template>
  </div>

  <!-- Devoluciones del proyecto -->
  <div id="refund-vouchers-section" class="mt-8 rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="flex items-center justify-between mb-6">
      <h4 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
        <svg class="h-6 w-6 text-indigo-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 15l6-6m-6 0l6 6M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        Devoluciones del Proyecto
      </h4>
      <div class="flex items-center space-x-4">
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Total Devoluciones</p>
          <p class="text-xl font-bold text-indigo-600 dark:text-indigo-400">{{totalRefundVouchers | currency}}</p>
        </div>
        <div class="h-8 w-px bg-gray-300 dark:bg-gray-600"></div>
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Registros</p>
          <p class="text-xl font-bold text-gray-900 dark:text-white">{{refundVouchersData?.length || 0}}</p>
        </div>
      </div>
    </div>

    <div *ngIf="refundVouchersData?.length > 0; else noRefundVouchers" class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20">
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                Participante & Tipo
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              Monto
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Estado & Banco
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                Fecha
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200 dark:bg-boxdark dark:divide-gray-700">
          <tr *ngFor="let refund of refundVouchersData" 
              class="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors duration-200">
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="flex items-center">
                <div class="h-10 w-10 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-full flex items-center justify-center text-white font-medium text-sm shadow-lg">
                  {{refund.participant?.firstName?.charAt(0)}}{{refund.participant?.lastName?.charAt(0)}}
                </div>
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900 dark:text-white">
                    {{refund.participant?.firstName}} {{refund.participant?.lastName}}
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">ID: {{refund.id}}</div>
                  <div class="mt-1">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
                      {{getRefundTypeName(refund.refundType)}}
                    </span>
                  </div>
                </div>
              </div>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                {{refund.refundAmount | currency}}
              </div>
            </td>
            
            <td class="px-4 py-5">
              <div class="space-y-2">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium" 
                    [ngClass]="{
                        'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400': refund.status === 'Pending',
                        'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400': refund.status === 'Approved',
                        'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400': refund.status === 'Processed',
                        'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400': refund.status === 'Rejected' || refund.status === 'Cancelled',
                        'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400': refund.status === 'BankReconciled'
                      }">
                  <svg class="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 8 8">
                    <circle cx="4" cy="4" r="3"/>
                  </svg>
                  {{getRefundStatusName(refund.status)}}
              </span>
                
                <div class="flex items-center text-sm text-gray-600 dark:text-gray-300" *ngIf="refund.bank?.name">
                  <svg class="h-4 w-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                  {{refund.bank?.name}}
                </div>
              </div>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="text-sm text-gray-900 dark:text-white">{{refund.requestDate | date:'short'}}</div>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <a [routerLink]="['/refund-vouchers/view', refund.id]" 
                 target="_blank"
                 class="inline-flex items-center px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 dark:text-indigo-400">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                Ver
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Estado vacío -->
    <ng-template #noRefundVouchers>
      <div class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 15l6-6m-6 0l6 6M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay devoluciones</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Este proyecto no tiene devoluciones registradas.</p>
      </div>
    </ng-template>
  </div>

  <!-- Gastos del proyecto -->
  <div id="expenses-section" class="mt-8 rounded-xl border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="flex items-center justify-between mb-6">
      <h4 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
        <svg class="h-6 w-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
        </svg>
        Gastos del Proyecto
      </h4>
      <div class="flex items-center space-x-4">
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Total Ejecutado</p>
          <p class="text-xl font-bold text-red-600 dark:text-red-400">{{totalExecutedAmount | currency}}</p>
        </div>
        <div class="h-8 w-px bg-gray-300 dark:bg-gray-600"></div>
        <div class="text-right">
          <p class="text-sm text-gray-500 dark:text-gray-400">Órdenes</p>
          <p class="text-xl font-bold text-gray-900 dark:text-white">{{workOrdersByProjectData?.length || 0}}</p>
        </div>
      </div>
    </div>

    <div *ngIf="workOrdersByProjectData?.length > 0; else noWorkOrders" class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20">
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                </svg>
                ID & Categoría
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
              Subcategoría
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              <div class="flex items-center">
                <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Ejecución
              </div>
            </th>
            <th class="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200 dark:bg-boxdark dark:divide-gray-700">
          <tr *ngFor="let order of workOrdersByProjectData" 
              class="hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors duration-200">
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="flex items-center">
                <div class="h-10 w-10 bg-gradient-to-br from-red-400 to-red-600 rounded-lg flex items-center justify-center text-white font-medium text-sm shadow-lg">
                  <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                  </svg>
                </div>
                <div class="ml-3">
                  <div class="text-sm font-medium text-gray-900 dark:text-white">
                    {{order.projectBudget.budgetSubcategory.budgetCategory.name}}
                  </div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">Orden #{{order.id}}</div>
                </div>
              </div>
            </td>
            
            <td class="px-4 py-5 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900 dark:text-white">
                {{order.projectBudget.budgetSubcategory.name}}
              </div>
              <!-- Relación con presupuesto -->
              <div class="mt-1 text-xs text-gray-500 dark:text-gray-400 flex items-center">
                <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                </svg>
                Presupuesto ID: {{order.projectBudget.id}}
              </div>
            </td>
            
            <td class="px-4 py-5">
              <div class="space-y-2">
                <div class="flex items-center justify-between bg-red-50 p-3 rounded-lg dark:bg-red-900/20">
                  <div>
                    <p class="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Monto Ejecutado</p>
                    <p class="text-lg font-bold text-red-900 dark:text-red-300">{{order.executedAmount | currency}}</p>
                  </div>
                  <div class="text-right">
                    <p class="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cantidad</p>
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">{{order.qty}}</p>
                  </div>
                </div>
                
                <!-- Indicador de eficiencia (monto por unidad) -->
                <div class="text-center" *ngIf="order.qty > 0">
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                    </svg>
                    {{(order.executedAmount / order.qty) | currency}} / unidad
                  </span>
                </div>
              </div>
            </td>

            <td class="px-4 py-5 whitespace-nowrap">
              <a [routerLink]="['/work-orders/view', order.id]" 
                 target="_blank"
                 class="inline-flex items-center px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors duration-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-400">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                Ver Orden
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Estado vacío -->
    <ng-template #noWorkOrders>
      <div class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">No hay gastos registrados</h3>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Este proyecto aún no tiene órdenes de trabajo o gastos ejecutados.</p>
      </div>
    </ng-template>
  </div>

<!-- Modal Section -->

<!-- Create Budget Project -->
<app-shared-modal [(isOpen)]="showModalNuevoPresupuesto">  
  <app-new-project-budget></app-new-project-budget>
</app-shared-modal>

<!-- Update Budget Project -->
<app-shared-modal [(isOpen)]="showModalEditarPresupuesto">  
  <app-new-project-budget [projectBudgetId]="projectBudgetId" (closeModal)="closeModalEditarPresupuesto()"></app-new-project-budget>
</app-shared-modal>


<!-- Edit Project  -->
 <app-shared-modal [(isOpen)]="showModalEditarProyecto">
  <app-edit-project [projectId]="projectId" (closeModal)="closeModalEditarProyecto()"></app-edit-project>
 </app-shared-modal>

<!-- 🆕 Modal de confirmación: eliminar rubro de presupuesto -->
<div *ngIf="showModalEliminarPresupuesto"
     class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
     (click)="closeModalEliminarPresupuesto()">
  <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md p-6"
       (click)="$event.stopPropagation()">
    <div class="flex items-start gap-3 mb-4">
      <div class="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
        <svg class="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        </svg>
      </div>
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Eliminar rubro de presupuesto</h3>
        <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Esta acción intentará eliminar el rubro
          <span class="font-medium">"{{ budgetToDelete?.budgetSubcategory?.name || 'seleccionado' }}"</span>.
          Sólo se podrá eliminar si no tiene comprobantes de ingreso ni órdenes de trabajo asociadas.
        </p>
      </div>
    </div>

    <div class="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-200 mb-4">
      <div class="flex justify-between">
        <span class="text-gray-500 dark:text-gray-400">ID:</span>
        <span class="font-medium">{{ budgetToDelete?.id }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-500 dark:text-gray-400">Categoría:</span>
        <span class="font-medium">{{ budgetToDelete?.budgetSubcategory?.budgetCategory?.name || '-' }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-500 dark:text-gray-400">Subcategoría:</span>
        <span class="font-medium">{{ budgetToDelete?.budgetSubcategory?.name || '-' }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-500 dark:text-gray-400">Proyectado:</span>
        <span class="font-medium">{{ budgetToDelete?.projectedAmount | currency:'COP':'symbol-narrow':'1.0-0' }}</span>
      </div>
    </div>

    <div class="flex justify-end gap-3">
      <button type="button"
              (click)="closeModalEliminarPresupuesto()"
              [disabled]="deletingBudget"
              class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50">
        Cancelar
      </button>
      <button type="button"
              (click)="confirmEliminarPresupuesto()"
              [disabled]="deletingBudget"
              class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:bg-red-300 disabled:cursor-not-allowed flex items-center">
        <span *ngIf="deletingBudget" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
        {{ deletingBudget ? 'Eliminando...' : 'Eliminar rubro' }}
      </button>
    </div>
  </div>
</div>